import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';

const GDPRComplianceSecurity = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('gdpr');

  const tabs = [
    { id: 'gdpr', label: 'GDPR', icon: 'Shield' },
    { id: 'encryption', label: 'Encryption', icon: 'Lock' },
    { id: 'certifications', label: 'Certifications', icon: 'Award' },
    { id: 'rights', label: 'Your Rights', icon: 'User' },
  ];

  return (
    <>
      <Helmet>
        <title>GDPR Compliance & Security - MyBodyToday™</title>
        <meta name="description" content="MyBodyToday GDPR compliance, AES-256 encryption standards, ISO 27001/SOC 2 certifications, and security audit details" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 pb-24">
        <BottomTabNavigation />

        {/* Header */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/settings-and-privacy')}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Back to Settings"
              >
                <Icon name="ArrowLeft" size={20} className="text-gray-600" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-teal-500/10 flex items-center justify-center">
                  <Icon name="ShieldCheck" size={18} className="text-teal-600" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-gray-900">GDPR Compliance & Security</h1>
                  <p className="text-xs text-gray-500">MyBodyToday&#8482; — Track small changes before they become big ones&#8482;</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Hero Trust Banner */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-6 mb-6 text-white">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icon name="ShieldCheck" size={28} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-1">Your Data is Protected</h2>
                <p className="text-teal-100 text-sm leading-relaxed">
                  MyBodyToday&#8482; is built with privacy-first architecture. We comply with GDPR, employ
                  military-grade AES-256 encryption, and undergo regular independent security audits to
                  keep your health data safe.
                </p>
              </div>
            </div>
            {/* Trust Badge Row */}
            <div className="flex flex-wrap gap-3 mt-5">
              <div className="flex items-center gap-2 bg-white/15 rounded-lg px-3 py-2">
                <Icon name="Shield" size={14} className="text-teal-200" />
                <span className="text-xs font-semibold text-white">GDPR Compliant</span>
              </div>
              <div className="flex items-center gap-2 bg-white/15 rounded-lg px-3 py-2">
                <Icon name="Lock" size={14} className="text-teal-200" />
                <span className="text-xs font-semibold text-white">AES-256 Encrypted</span>
              </div>
              <div className="flex items-center gap-2 bg-white/15 rounded-lg px-3 py-2">
                <Icon name="Award" size={14} className="text-teal-200" />
                <span className="text-xs font-semibold text-white">ISO 27001 Aligned</span>
              </div>
              <div className="flex items-center gap-2 bg-white/15 rounded-lg px-3 py-2">
                <Icon name="CheckCircle" size={14} className="text-teal-200" />
                <span className="text-xs font-semibold text-white">SOC 2 Aligned</span>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap flex-1 justify-center ${
                  activeTab === tab?.id
                    ? 'bg-white text-teal-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon name={tab?.icon} size={15} />
                {tab?.label}
              </button>
            ))}
          </div>

          {/* GDPR Tab */}
          {activeTab === 'gdpr' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <Icon name="Globe" size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">GDPR Compliance</h3>
                    <p className="text-xs text-gray-500">General Data Protection Regulation</p>
                  </div>
                  <div className="ml-auto">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-semibold border border-green-200">
                      <Icon name="CheckCircle" size={12} />
                      Compliant
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  MyBodyToday&#8482; fully complies with the EU General Data Protection Regulation (GDPR) and the UK GDPR
                  post-Brexit. We treat your health data as special category data under Article 9 and apply the
                  highest level of protection.
                </p>
                <div className="space-y-3">
                  {[
                    { icon: 'CheckCircle', text: 'Lawful basis for processing established under Article 6 & 9 GDPR', color: 'text-green-600' },
                    { icon: 'CheckCircle', text: 'Data minimisation — we only collect what is strictly necessary', color: 'text-green-600' },
                    { icon: 'CheckCircle', text: 'Purpose limitation — data used only for health tracking purposes', color: 'text-green-600' },
                    { icon: 'CheckCircle', text: 'Storage limitation — data retained only as long as necessary', color: 'text-green-600' },
                    { icon: 'CheckCircle', text: 'Data subject rights fully implemented (access, erasure, portability)', color: 'text-green-600' },
                    { icon: 'CheckCircle', text: 'Privacy by design and by default embedded in architecture', color: 'text-green-600' },
                    { icon: 'CheckCircle', text: 'Data Protection Impact Assessment (DPIA) conducted', color: 'text-green-600' },
                    { icon: 'CheckCircle', text: 'Breach notification procedures in place (72-hour reporting)', color: 'text-green-600' },
                  ]?.map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Icon name={item?.icon} size={16} className={`${item?.color} flex-shrink-0 mt-0.5`} />
                      <p className="text-sm text-gray-700">{item?.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                    <Icon name="Database" size={20} className="text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Data Protection Measures</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { icon: 'Lock', title: 'End-to-End Encryption', desc: 'All health data encrypted in transit and at rest' },
                    { icon: 'Server', title: 'EU Data Residency', desc: 'Data stored on EU-based servers by default' },
                    { icon: 'Eye', title: 'Access Controls', desc: 'Role-based access with audit logging' },
                    { icon: 'Trash2', title: 'Right to Erasure', desc: 'Complete data deletion on account closure' },
                    { icon: 'Download', title: 'Data Portability', desc: 'Export your data in JSON or CSV format' },
                    { icon: 'UserX', title: 'Anonymisation', desc: 'Analytics use anonymised, aggregated data only' },
                  ]?.map((item, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm">
                        <Icon name={item?.icon} size={16} className="text-teal-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{item?.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{item?.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Encryption Tab */}
          {activeTab === 'encryption' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center">
                    <Icon name="Lock" size={20} className="text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">AES-256 Encryption</h3>
                    <p className="text-xs text-gray-500">Military-grade data protection</p>
                  </div>
                  <div className="ml-auto">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-semibold border border-teal-200">
                      <Icon name="ShieldCheck" size={12} />
                      Active
                    </span>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  We use AES-256 (Advanced Encryption Standard with 256-bit keys) — the same encryption standard
                  used by governments, militaries, and financial institutions worldwide. Your health data is
                  unreadable to anyone without your unique encryption key.
                </p>
                <div className="bg-teal-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-teal-700">256-bit</p>
                      <p className="text-xs text-teal-600">Key Length</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-teal-700">AES</p>
                      <p className="text-xs text-teal-600">Algorithm</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-teal-700">TLS 1.3</p>
                      <p className="text-xs text-teal-600">In Transit</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-teal-700">At Rest</p>
                      <p className="text-xs text-teal-600">Storage Encrypted</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { icon: 'Lock', text: 'Symptom entries encrypted before database storage' },
                    { icon: 'Lock', text: 'Personal health notes encrypted with user-specific keys' },
                    { icon: 'Lock', text: 'All API communications secured with TLS 1.3' },
                    { icon: 'Lock', text: 'Encryption keys managed via secure key management service' },
                    { icon: 'Lock', text: 'Zero-knowledge architecture — staff cannot read your health data' },
                  ]?.map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <Icon name={item?.icon} size={16} className="text-teal-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-700">{item?.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
                    <Icon name="Search" size={20} className="text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Security Audit Details</h3>
                </div>
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-900">Penetration Testing</p>
                      <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">Passed</span>
                    </div>
                    <p className="text-xs text-gray-500">Independent third-party penetration testing conducted annually to identify and remediate vulnerabilities before they can be exploited.</p>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-900">Vulnerability Scanning</p>
                      <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">Continuous</span>
                    </div>
                    <p className="text-xs text-gray-500">Automated vulnerability scanning runs continuously across all infrastructure components, with critical issues addressed within 24 hours.</p>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-900">Code Security Review</p>
                      <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">Ongoing</span>
                    </div>
                    <p className="text-xs text-gray-500">All code changes undergo security review before deployment. Static analysis tools scan for common vulnerabilities (OWASP Top 10).</p>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-semibold text-gray-900">Incident Response Plan</p>
                      <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-0.5 rounded-full">In Place</span>
                    </div>
                    <p className="text-xs text-gray-500">Documented incident response procedures with defined escalation paths, communication protocols, and post-incident review processes.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Certifications Tab */}
          {activeTab === 'certifications' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                    <Icon name="Award" size={20} className="text-yellow-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Privacy Certifications & Standards</h3>
                </div>
                <div className="space-y-4">
                  <div className="border-2 border-blue-100 bg-blue-50 rounded-xl p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon name="Shield" size={22} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-bold text-blue-900">ISO 27001 Aligned</h4>
                          <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full font-medium">Information Security</span>
                        </div>
                        <p className="text-sm text-blue-800 leading-relaxed">
                          Our information security management practices are aligned with ISO/IEC 27001 standards —
                          the international standard for information security management systems (ISMS). This covers
                          risk assessment, security controls, and continuous improvement.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {['Risk Management', 'Access Control', 'Incident Management', 'Business Continuity']?.map((tag) => (
                            <span key={tag} className="text-xs bg-white text-blue-700 px-2 py-1 rounded-lg border border-blue-200">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-2 border-purple-100 bg-purple-50 rounded-xl p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon name="CheckSquare" size={22} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-bold text-purple-900">SOC 2 Aligned</h4>
                          <span className="text-xs bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full font-medium">Service Organisation Control</span>
                        </div>
                        <p className="text-sm text-purple-800 leading-relaxed">
                          Our security practices align with SOC 2 Trust Service Criteria, covering Security,
                          Availability, Processing Integrity, Confidentiality, and Privacy. This demonstrates
                          our commitment to protecting customer data.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {['Security', 'Availability', 'Confidentiality', 'Privacy']?.map((tag) => (
                            <span key={tag} className="text-xs bg-white text-purple-700 px-2 py-1 rounded-lg border border-purple-200">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="border-2 border-green-100 bg-green-50 rounded-xl p-5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon name="Globe" size={22} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-bold text-green-900">UK GDPR & EU GDPR</h4>
                          <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-medium">Data Protection</span>
                        </div>
                        <p className="text-sm text-green-800 leading-relaxed">
                          Fully compliant with both UK GDPR (post-Brexit) and EU GDPR. We maintain a lawful basis
                          for all data processing, honour all data subject rights, and have appointed a Data
                          Protection Officer (DPO) responsible for compliance oversight.
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {['UK GDPR', 'EU GDPR', 'DPO Appointed', 'Article 9 Compliant']?.map((tag) => (
                            <span key={tag} className="text-xs bg-white text-green-700 px-2 py-1 rounded-lg border border-green-200">{tag}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Your Rights Tab */}
          {activeTab === 'rights' && (
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center">
                    <Icon name="User" size={20} className="text-teal-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">Your Rights Under GDPR</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Under GDPR, you have the following rights regarding your personal health data. You can exercise
                  any of these rights by contacting us at <a href="mailto:privacy@mybodytoday.com" className="text-teal-600 font-medium">privacy@mybodytoday.com</a>.
                </p>
                <div className="space-y-3">
                  {[
                    {
                      icon: 'Eye',
                      title: 'Right of Access (Article 15)',
                      desc: 'You have the right to request a copy of all personal data we hold about you, including your health tracking data, account information, and usage logs.',
                      action: 'Request via Settings → Export Data'
                    },
                    {
                      icon: 'Edit',
                      title: 'Right to Rectification (Article 16)',
                      desc: 'You have the right to correct inaccurate personal data or complete incomplete data we hold about you.',
                      action: 'Update in Profile Settings'
                    },
                    {
                      icon: 'Trash2',
                      title: 'Right to Erasure (Article 17)',
                      desc: 'You have the right to request deletion of your personal data ("right to be forgotten"). We will delete all your data within 30 days.',
                      action: 'Settings → Delete Account'
                    },
                    {
                      icon: 'Pause',
                      title: 'Right to Restrict Processing (Article 18)',
                      desc: 'You can request that we restrict how we process your data while a dispute is being resolved.',
                      action: 'Contact privacy@mybodytoday.com'
                    },
                    {
                      icon: 'Download',
                      title: 'Right to Data Portability (Article 20)',
                      desc: 'You can request your data in a machine-readable format (JSON or CSV) to transfer to another service.',
                      action: 'Settings → Export Data (JSON/CSV)'
                    },
                    {
                      icon: 'XCircle',
                      title: 'Right to Object (Article 21)',
                      desc: 'You can object to processing of your data for direct marketing or profiling purposes at any time.',
                      action: 'Settings → Notification Preferences'
                    },
                  ]?.map((right, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon name={right?.icon} size={16} className="text-teal-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-900 mb-1">{right?.title}</p>
                          <p className="text-xs text-gray-600 mb-2 leading-relaxed">{right?.desc}</p>
                          <div className="flex items-center gap-1.5">
                            <Icon name="ArrowRight" size={12} className="text-teal-500" />
                            <p className="text-xs text-teal-600 font-medium">{right?.action}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-teal-50 border border-teal-200 rounded-xl p-5">
                <div className="flex items-start gap-3">
                  <Icon name="Mail" size={20} className="text-teal-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-teal-900 mb-1">Contact Our Data Protection Officer</h4>
                    <p className="text-sm text-teal-800 mb-3">
                      For any privacy concerns, data requests, or to exercise your GDPR rights, contact our DPO:
                    </p>
                    <a href="mailto:privacy@mybodytoday.com" className="inline-flex items-center gap-2 text-sm font-medium text-teal-700 hover:text-teal-900">
                      <Icon name="Mail" size={14} />
                      privacy@mybodytoday.com
                    </a>
                    <p className="text-xs text-teal-600 mt-2">Response time: within 30 days as required by GDPR</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Footer note */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-400">© 2025 MyBodyToday&#8482;. All rights reserved.</p>
            <p className="text-xs text-gray-400 mt-0.5">Track small changes before they become big ones&#8482;</p>
          </div>
        </main>
      </div>
    </>
  );
};

export default GDPRComplianceSecurity;
